/// <reference types="vite/client" />

import { Link, useNavigate } from 'react-router-dom'
import React, { type FormEvent } from 'react'
import { CredentialResponse, GoogleLogin } from '@react-oauth/google'
import { AuthShell } from './AuthShell'
import { apiPost } from '../../lib/api'
import { AUTH_GOOGLE_REGISTER, AUTH_REGISTER } from '../../lib/endpoints'
import { colors } from '../../config/themeAuth'
import { saveAuth } from '../../lib/authStorage'

type ApiResponse = {
  status: number
  message: string
}

type AuthResponse = {
  accessToken: string
  refreshToken: string
  role: string
  onboardingCompleted?: boolean
}

export default function SignUp() {
  const navigate = useNavigate()
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
  const [firstName, setFirstName] = React.useState('')
  const [lastName, setLastName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [showPassword, setShowPassword] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState<string | null>(null)

  const passwordsMatch = password.length > 0 && password === confirmPassword
  const canSubmit = React.useMemo(() => {
    return (
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      email.trim().length > 3 &&
      password.length >= 6 &&
      passwordsMatch
    )
  }, [firstName, lastName, email, password, passwordsMatch])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setLoading(true)
    try {
      const res = await apiPost<
        ApiResponse,
        {
          firstName: string
          lastName: string
          email: string
          password: string
          confirmPassword: string
        }
      >(AUTH_REGISTER, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        confirmPassword,
      })
      setSuccess('Account created. Check your email for the verification code.')
      // Navigate to verification page (email prefilled)
      navigate(`/verify-email?email=${encodeURIComponent(email.trim())}`, { replace: true })
      return res
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign up failed')
    } finally {
      setLoading(false)
    }
  }

  async function handleGoogleSuccess(credentialResponse: CredentialResponse) {
    const idToken = credentialResponse.credential
    if (!idToken) {
      setError('Google sign-up did not return a token. Please try again.')
      return
    }
    setError(null)
    setSuccess(null)
    setLoading(true)
    try {
      const res = await apiPost<AuthResponse, { idToken: string }>(AUTH_GOOGLE_REGISTER, {
        idToken,
      })
      saveAuth({ accessToken: res.accessToken, refreshToken: res.refreshToken, role: res.role })
      const dest = res.role === 'ADMIN' || res.onboardingCompleted ? '/dashboard' : '/'
      navigate(dest, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-up failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle="Sign up with email (OTP verification) or Google."
    >
      {googleClientId ? (
        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError('Google sign-up failed')}
            theme="outline"
            size="large"
            shape="pill"
            text="signup_with"
            logo_alignment="left"
            width={360}
          />
        </div>
      ) : (
        <div
          className="rounded-2xl border px-4 py-3 text-xs"
          style={{ borderColor: colors.border, color: colors.muted }}
        >
          Google sign-up is not configured. Set <code>VITE_GOOGLE_CLIENT_ID</code> to enable it.
        </div>
      )}

      <div className="my-5 flex items-center gap-3">
        <div className="h-px flex-1" style={{ backgroundColor: colors.border }} />
        <span className="text-xs" style={{ color: colors.muted }}>
          or
        </span>
        <div className="h-px flex-1" style={{ backgroundColor: colors.border }} />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium" style={{ color: colors.ink }}>
              First name
            </label>
            <input
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              className="nudge-input mt-2 w-full rounded-2xl border px-4 py-3 text-sm"
              style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}
              type="text"
              autoComplete="given-name"
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium" style={{ color: colors.ink }}>
              Last name
            </label>
            <input
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              className="nudge-input mt-2 w-full rounded-2xl border px-4 py-3 text-sm"
              style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}
              type="text"
              autoComplete="family-name"
              required
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium" style={{ color: colors.ink }}>
            Email
          </label>
          <input
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="nudge-input mt-2 w-full rounded-2xl border px-4 py-3 text-sm"
            style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            required
          />
        </div>

        <div>
          <label className="text-sm font-medium" style={{ color: colors.ink }}>
            Password
          </label>
          <div className="relative mt-2">
            <input
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="nudge-input w-full rounded-2xl border px-4 py-3 pr-12 text-sm"
              style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="At least 6 characters"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(s => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold"
              style={{ color: colors.muted }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium" style={{ color: colors.ink }}>
            Confirm password
          </label>
          <input
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            className="nudge-input mt-2 w-full rounded-2xl border px-4 py-3 text-sm"
            style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}
            type="password"
            autoComplete="new-password"
            required
          />
          {!passwordsMatch && confirmPassword.length > 0 ? (
            <p className="mt-2 text-xs" style={{ color: colors.darkOrange }}>
              Passwords don’t match.
            </p>
          ) : null}
        </div>

        {error ? (
          <div
            className="rounded-2xl border px-4 py-3 text-sm"
            style={{
              borderColor: colors.border,
              color: colors.darkOrange,
              backgroundColor: colors.sand,
            }}
          >
            {error}
          </div>
        ) : null}

        {success ? (
          <div
            className="rounded-2xl border px-4 py-3 text-sm"
            style={{ borderColor: colors.border, color: colors.ink, backgroundColor: colors.sand }}
          >
            {success}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={!canSubmit || loading}
          className="w-full rounded-2xl px-4 py-3 text-sm font-semibold transition-all disabled:opacity-60"
          style={{ backgroundColor: colors.ctaBg, color: colors.ctaColor }}
        >
          {loading ? 'Creating…' : 'Create account'}
        </button>
      </form>

      <p className="mt-6 text-sm" style={{ color: colors.muted }}>
        Already have an account?{' '}
        <Link to="/signin" className="font-semibold underline" style={{ color: colors.ink }}>
          Sign in
        </Link>
      </p>
    </AuthShell>
  )
}
